import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ApiClientService } from '../../../core/api-client.service';

export interface BookingStaff {
  branchStaffID: number;
  staffID?: number;
  name?: string;
  surname?: string;
  title?: string;
  professionName?: string;
  media?: string | null;
  imageUrl?: string | null;
  available?: boolean | number;
}

export interface BookingService {
  id: number;
  title: string;
  description?: string;
  duration?: number;
  price?: number;
  currency?: string;
  media?: string | null;
  imageUrl?: string | null;
}

export interface BookingAvailability {
  branchStaffID: number;
  listings: Array<{ listingID: number; duration: number }>;
  workingHours: Record<string, Array<{ startTime: string; endTime: string }>>;
  appointments: Array<{ startTime: string; endTime: string }>;
}

export interface AppointmentVerification {
  temporaryID: number;
  recipientID?: string;
  maskedPhone?: string;
}

export interface BookingStartResult {
  draftID: number;
  workingHours: Record<string, Array<{ startTime: string; endTime: string; isClosed?: boolean | number }>>;
}

export interface AppointmentResult {
  appointmentID?: number;
  manageCode?: string;
  manageUrl?: string;
  branchName?: string;
  branchAddress?: string;
  branchMapsUrl?: string;
  staffName?: string;
  listingNames?: string[];
  appointmentDate?: string;
  startTime?: string;
  endTime?: string;
  googleCalendarUrl?: string;
  calendarTitle?: string;
  calendarDescription?: string;
  deposit?: {
    required?: boolean;
    amount?: number;
    currency?: string;
    reference?: string;
    mirrorEnabled?: boolean;
    bankTransferEnabled?: boolean;
    commissionRate?: number;
    paymentLink?: string;
    bankName?: string;
    accountHolder?: string;
    iban?: string;
  } | null;
}

export interface BookingCustomerProfile {
  profileID: number;
  fullName?: string;
  phone?: string;
  email?: string;
}

@Injectable({ providedIn: 'root' })
export class AppointmentBookingService {
  private readonly api = inject(ApiClientService);

  async start(branchID: number): Promise<BookingStartResult> {
    const response = await this.post('appointmentStart', { branchID });
    const calendar = this.value(response, 'calendar') as Record<string, unknown> | undefined;
    return {
      draftID: Number(this.value(response, 'draftID') ?? 0),
      workingHours: (calendar?.['workingHours'] || {}) as BookingStartResult['workingHours']
    };
  }

  async staff(draftID: number, branchID: number, date: string): Promise<BookingStaff[]> {
    const response = await this.post('appointmentStaff', { draftID, branchID, date });
    return this.array<BookingStaff>(response, 'staff');
  }

  async services(draftID: number, branchID: number, date: string, branchStaffID: number): Promise<BookingService[]> {
    const response = await this.post('appointmentServices', { draftID, branchID, date, branchStaffID });
    return this.array<BookingService>(response, 'listings');
  }

  async availability(
    draftID: number,
    branchID: number,
    date: string,
    branchStaffID: number,
    listingIDs: number[]
  ): Promise<BookingAvailability | null> {
    const listings = Object.fromEntries(listingIDs.map((id) => [String(id), true]));
    const response = await this.post('appointmentAvailability', { draftID, branchID, date, branchStaffID, listings });
    return this.value(response, 'availability') as BookingAvailability | null;
  }

  async selectTime(draftID: number, date: string, time: string): Promise<boolean> {
    const response = await this.post('appointmentSelectTime', { draftID, date, time });
    return Boolean(this.value(response, 'time'));
  }

  async submit(payload: Record<string, unknown>): Promise<{
    verification?: AppointmentVerification;
    completed?: AppointmentResult;
    message?: string;
  }> {
    const response = await this.post('appointmentSubmit', payload);
    return {
      verification: this.value(response, 'appointmentPhoneVerification') as AppointmentVerification | undefined,
      completed: this.value(response, 'appointmentPhoneVerificationCompleted') as AppointmentResult | undefined,
      message: String(this.value(response, 'message') ?? '')
    };
  }

  async customerProfiles(): Promise<{ profiles: BookingCustomerProfile[]; phoneVerified: boolean }> {
    const response = await this.post('/api/appointments/customer-profiles', {});
    return {
      profiles: this.array<BookingCustomerProfile>(response, 'profiles'),
      phoneVerified: Boolean(this.value(response, 'phoneVerified'))
    };
  }

  private async post(key: string, payload: Record<string, unknown>): Promise<unknown> {
    return firstValueFrom(this.api.post<unknown>(key, payload));
  }

  private array<T>(source: unknown, key: string): T[] {
    const value = this.value(source, key);
    return Array.isArray(value) ? value as T[] : [];
  }

  private value(source: unknown, key: string): unknown {
    if (!source || typeof source !== 'object') return undefined;
    const record = source as Record<string, unknown>;
    if (Object.prototype.hasOwnProperty.call(record, key)) return record[key];
    for (const childKey of ['data', 'apiData', 'response']) {
      const found = this.value(record[childKey], key);
      if (found !== undefined) return found;
    }
    return undefined;
  }
}
